import { GlobalContext } from "../GlobalContext";

export class UrlUtils {
    private static readonly _URL_EXP: RegExp = new RegExp(
        "^" +
        "(" +
            // Povolení 0 až 5 lomítek u protokolu file
            "(file:(/){0,5})" +
            "|" +
            // Identifikátor protokolu
            "([-a-zA-Z0-9]+://)" +
        ")" +
        // Autentizace (user:password@)
        "(?:\\S+(?::\\S*)?@)?" +
        "(?:" +
            // IP adresa, mimo rezervované (0.0.0.0-1.0.0.0, 224.0.0.0-255.255.255.255, #.#.#.0, #.#.#.255)
            "(?:[1-9]|[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])" + // 1-223
            "(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}\\." + // 2x 0-225
            "(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4])" + // 1-224
            "|" +
            // Host
            // Celková délka doménového jména nesmí být delší než 253 znaků
            // Žádná část doménového jména nesmí: být delší než 63 znaků, začínat ani končit pomlčkou, obsahovat více pomlček za sebou
            "(?![^:/#?]{254,})" +
            // Poslední subdoména
            "(?![^.:/#?]{64,})" +
            /* "(?:xn--)?" +*/ // Povolí punycode
            "[a-z\\u00a1-\\uffff0-9]+(?:-[a-z\\u00a1-\\uffff0-9]+)*" +
            "(?:\\." +
                // Ostatní subdoméney
                "(?:" +
                    "(?![^.:/#?]{64,})" +
                    /* "(?:xn--)?" +*/ // Povolí punycode
                    "[a-z\\u00a1-\\uffff0-9]+(?:-[a-z\\u00a1-\\uffff0-9]+)*\\." +
                ")*" +
                // Top-level doména (oproti subdoménám musí navíc obsahovat alespoň jeden nečíselný znak)
                "(?![^.:/#?]{64,})" +
                "(?!\\d+(?:$|[:/#?]))" +
                /* "(?:xn--)?" +*/ // Povolí punycode
                "[a-z\\u00a1-\\uffff0-9]+(?:-[a-z\\u00a1-\\uffff0-9]+)*\\.?" +
            ")?" +
        ")" +
        // Číslo portu
        "(?::\\d{2,5})?" +
        // Cesta začínající lomítkem, nebo query string otazníkem, nebo fragment mřížkou
        "(?:[/#?]\\S*)?" +
        "$", "i",
    );

    private static readonly _EMAIL_EXP: RegExp = /^((\bmailto:){0,1}[\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/i;

    private static readonly _PARAMETRIC_MAILTO_EXP: RegExp = /^((\bmailto:)[\w@-]+(?:\.[\w@-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([\w@]+)$/i;

    // Začíná "X:\" nebo "\\host" nebo "//" za kterými může následovat cesta k souboru\adresáři např "adresář\adresář\soubor" nebo "/adresář/adresář/soubor".
    private static readonly _FILE_EXP: RegExp = /^(?:[a-zA-Z]:\\|\\\\([\w\s0-9._-]+)*($|\\)|\/\/|\/([\w0-9á-žÁ-Ž_-][\w\s0-9á-žÁ-Ž_-]*)($|\/))((?:[^\s<>:/"\\|?*][^<>:"/\\|?*]*)(?:(\\|\/)[^\s<>:"/\\|?*][^<>:"/\\|?*]*)*[\\/]?)?$/iu;
    /**
     * Najde hodnotu parametru v aktuální adrese nebo v předané url
     * @param name Název hledaného atributu
     * @param url Url, ve které se má atribut hladat - pokud není uvedená, tak se hledá v aktuální adrese
     */
    public static getParameterByName(name: string, url?: string): string {
        name = name.replace(/[[]/, "\\[").replace(/[\]]/, "\\]");

        if (!url) {
            url = location.search;
        } else {
            const paramsStart = url.indexOf("?");
            if (paramsStart !== -1)
                url = url.substr(paramsStart);
        }

        const regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
        const results = regex.exec(url);
        return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
    }

    /**
     * Odstraní z předané url poslední část se souborem.
     * Pokud url soubor neobsahuje, tak se url nemění.
     * KOPIE pro skripty ve script-utils.js - při změně nutno synchronizovat!
     * @example Z http://domain.xy/b/index.html vytvoří http://domain.xy/b/.
     * @param url Url k ořežání
     */
    public static stripFilePart(url: URL): URL {
        if (!url.pathname)
            return url;

        const filePartStart = url.pathname.lastIndexOf("/");
        const filePart = filePartStart === -1 ? url.pathname : url.pathname.substring(filePartStart);
        if (!/([\d\w-_]+\.[\d\w]+)/i.test(filePart))
            return url;

        return new URL(url.pathname.substring(0, filePartStart + 1), url.origin);
    }

    /**
     * Zparsuje url z textové podoby na URL objekt.
     * Je možné zadat absolutní url začínající na schéma a nebo zadat relativní url.
     * Relativní url se připojí k parametru baseUrl.
     * @example Relativní url "../.." a baseUrl "http://domain.xy/a/b/c/" vytvoří "http://domain.xy/a/".
     * @param url Url ke zpracování
     * @param baseUrl Základní url pro relativní adresy
     */
    public static parseUrl(url: string, baseUrl: URL): URL | undefined {
        if (!url)
            return undefined;

        // absolutní url
        if (/^https?:\/\//i.test(url))
            return new URL(url.endsWith("/") ? url : `${url}/`);

        // relativní url
        return new URL(url, UrlUtils.stripFilePart(baseUrl));
    }

    /**
     * Funkce testuje zda je adresa parametrická.
     * @param url Testovaná url.
     * @returns {boolean} - Vrací true v případě, že jde o parametrickou adresu.
     */
    public static isParametricUrl(url: string): boolean {
        return this.isUrl(url) || this.isParametricMailto(url) || this.isFile(url);
    }

    /**
     * Testuje zda je řetězec url adresa.
     * @param value Řetězec k otestování.
     * @returns {boolean} - Vrací true pokud se jedná o url adresu.
     */
    public static isUrl(value: string): boolean {
        return this._URL_EXP.test(value);
    }

    /**
     * Testuje zda je řetězec emailová adresa.
     * @param value Řetězec k otestování.
     * @returns {boolean} - Vrací true pokud se jedná o emailovou adresu.
     */
    public static isEmail(value: string): boolean {
        return this._EMAIL_EXP.test(value);
    }

    /**
     * Testuje zda je řetězec parametrická emailová adresa s "mailto".
     * @param value Řetězec k otestování.
     * @returns {boolean} - Vrací true pokud se jedná o parametrickou emailovou adresu s "mailto".
     */
    public static isParametricMailto(value: string): boolean {
        return this._PARAMETRIC_MAILTO_EXP.test(value);
    }

    /**
     * Testuje zda je řetězec cesta k souboru.
     * @param value Řetězec k otestování.
     * @returns {boolean} - Vrací true pokud se jedná o cestu k souboru.
     */
    public static isFile(value: string): boolean {
        return this._FILE_EXP.test(value);
    }

    /**
     * Funkce testuje zda adresa obsahuje parametry.
     * @param url Testovaná url.
     * @returns {boolean} - Vrací true v případě, že adresa obsahuje parametry.
     */
    public static hasParameters(url: string): boolean {
        const isParametricMailto = this.isParametricMailto(url);
        const atCount = url.split("@").length;

        if ((isParametricMailto && atCount > 2) || (!isParametricMailto && atCount > 1))
            return true;

        return false;
    }
}

GlobalContext.registerType(UrlUtils);
